import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { generatePathResourceDetail } from '../../../app/routing/routes.config.js';
import { embedV2Version } from '../../../Tiptap/Extensions/EmbedResource/EmbedExtension.js';
import copyToClipboard from 'copy-to-clipboard';
import dayjs from 'dayjs';
import { styled, css } from 'styled-components';
import { ChatbotConversationUserActionType, ChatbotConversationUserActionSubType } from '../../types.js';
import { useMutationCreateResourceV2 } from '../../../resources/mutations/useMutationCreateResourceV2.js';
import { useChatbot } from '../../hooks/useChatbot.js';
import { useDestinationParentFolderFromPathname } from '../../../resources/components/NewResource/useDestinationParentFolderFromPathname.js';
import { Flex } from '@fabric-app/ui/components/Flex';
import { toast } from '../../../store/alerts.js';
import { markdownToPlainText, parseMarkdown } from '@fabric-app/ui/markdown/markdown.utils';
import { Button } from '@fabric-app/ui/components/Button';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { isChatbotAssistantMessage, isChatbotUserMessage } from '../../utils/guardsChatbotMessage.js';
import { dispatchFabricEvent } from '../../../hooks/useFabricEvent.js';
import { IconSidePanel20Tick, IconSidePanel20Close } from '@fabric-app/icons/svg';
import { IconReload } from '@fabric-app/icons/old';
import { useIsFeatureFlagEnabled, FeatureFlag } from '../../../feature-flags/feature-flags.store.js';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';
import { useAuthStatus } from '@fabric-app/compat';
import { getYdocBinaryStateFromHTML } from '../../../Tiptap/utils/getEmptyYdocBinaryState.js';

const replaceAnchorsWithNotepadExtensionEmbedElement = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const anchorTags = doc.querySelectorAll("a");
  anchorTags.forEach((a) => {
    const href = a.getAttribute("href");
    if (href && href.startsWith("fabric://")) {
      const span = document.createElement("span");
      span.textContent = a.textContent;
      span.setAttribute("data-resource-id", href.split("://")[1]);
      span.setAttribute("data-node-type", "embedV2");
      span.setAttribute("data-version", embedV2Version);
      a.replaceWith(span);
    }
  });
  return doc.body.innerHTML;
};
const replaceMarkdownFabricResourceLinks = (text) => {
  let result = text;
  const rg = new RegExp(/\[(.*?)\]\(fabric:\/\/(.*?)\).*/gm);
  const linkedItems = /* @__PURE__ */ new Map();
  let itemExecRes = null;
  while ((itemExecRes = rg.exec(result)) !== null) {
    linkedItems.set(itemExecRes[2], itemExecRes[1]);
  }
  linkedItems.forEach((value, key) => {
    result = result.replace(`[${value}](fabric://${key})`, `${value} (${window.location.origin}${generatePathResourceDetail(key)})`);
  });
  return result;
};
const ActionGroup = styled(Flex).attrs((p) => {
  return {
    ...p,
    gap: 12,
    justifyContent: "flex-end",
    maxWidth: "100%",
    minWidth: 0
  };
}).withConfig({
  displayName: "ChatbotMessageActions__ActionGroup",
  componentId: "sc-a34732c0-0"
})([
  ``
]);
const ChatbotMessageActions = ({ message, style }) => {
  const { mutate: mutateCreateResource, isPending: isSavingAsNote } = useMutationCreateResourceV2();
  const { addMessageSentAsAssistant, continueMessage, addMessage, diffsActiveMessageId, messages, expandedResource } = useChatbot();
  const lastMessageId = messages[messages.length - 1]?.id;
  const allowRepeatingAssistantDiffs = useIsFeatureFlagEnabled(FeatureFlag.ALLOW_REPEATING_ASSISTANT_DIFFS);
  const repeatDiffs = isChatbotAssistantMessage(message) && message.diffs && message.diffs.length > 0 && allowRepeatingAssistantDiffs && message.diffsResourceId && expandedResource?.id === message.diffsResourceId ? /* @__PURE__ */ jsx(ButtonMessageAction, {
    size: "sm",
    onClick: () => {
      dispatchFabricEvent("fabric-ai-diff-propose", {
        messageId: message.id,
        diffs: message.diffs,
        override: true,
        resourceId: message.diffsResourceId
      });
    },
    children: /* @__PURE__ */ jsx(IconReload, {})
  }) : null;
  const content = isChatbotUserMessage(message) && message.action?.type === ChatbotConversationUserActionType.SUMMARIZE && message.action.subType === ChatbotConversationUserActionSubType.TEXT ? `Summarize highlight:
_“${message.content}”_` : message.content;
  const destinationParentFolder = useDestinationParentFolderFromPathname();
  const authStatus = useAuthStatus();
  if (isChatbotAssistantMessage(message) && message.streaming) return null;
  if (isChatbotAssistantMessage(message) && message.hasFailedDiffs && lastMessageId === message.id) return /* @__PURE__ */ jsxs(ActionGroup, {
    style,
    children: [
      repeatDiffs,
      /* @__PURE__ */ jsx(ButtonMessageAction, {
        size: "sm",
        onClick: () => addMessage("Retry", void 0, "Editor failed to apply changes, please do it again."),
        children: "Retry"
      })
    ]
  });
  if (isChatbotAssistantMessage(message) && message.hasIncompleteDiffs && lastMessageId === message.id && expandedResource?.id === message.diffsResourceId && message.diffsResourceId) return /* @__PURE__ */ jsx(ActionGroup, {
    style,
    children: /* @__PURE__ */ jsx(ButtonMessageAction, {
      size: "sm",
      onClick: () => continueMessage(message),
      children: "Continue"
    })
  });
  return /* @__PURE__ */ jsx(ScrollArea.Default, {
    orientation: "horizontal",
    scrollbarVariant: "none",
    style: {
      width: "100%",
      minWidth: 0,
      display: "grid"
    },
    children: /* @__PURE__ */ jsx(ActionGroup, {
      style,
      children: isChatbotAssistantMessage(message) && message.id === diffsActiveMessageId && message.diffs && message.diffs.length > 0 && expandedResource?.id === message.diffsResourceId && message.diffsResourceId ? /* @__PURE__ */ jsxs(Fragment, {
        children: [
          /* @__PURE__ */ jsxs(ButtonMessageAction, {
            size: "sm",
            onClick: () => {
              dispatchFabricEvent("fabric-ai-diff-apply", {
                messageId: message.id,
                action: "apply",
                source: "assistant",
                resourceId: message.diffsResourceId
              });
            },
            diffApplyType: "accept",
            children: [
              /* @__PURE__ */ jsx(IconSidePanel20Tick, {}),
              "Accept changes"
            ]
          }),
          /* @__PURE__ */ jsxs(ButtonMessageAction, {
            size: "sm",
            onClick: () => {
              dispatchFabricEvent("fabric-ai-diff-apply", {
                messageId: message.id,
                action: "reject",
                source: "assistant",
                resourceId: message.diffsResourceId
              });
            },
            diffApplyType: "reject",
            children: [
              /* @__PURE__ */ jsx(IconSidePanel20Close, {}),
              "Reject changes"
            ]
          })
        ]
      }) : /* @__PURE__ */ jsxs(Fragment, {
        children: [
          repeatDiffs,
          /* @__PURE__ */ jsx(ButtonMessageAction, {
            size: "sm",
            onClick: (e) => {
              e.stopPropagation();
              markdownToPlainText(content, {
                keep: [
                  "link"
                ]
              }).then(replaceMarkdownFabricResourceLinks).then(copyToClipboard).then((isCopied) => {
                if (isCopied) {
                  toast({
                    content: "Copied to clipboard",
                    id: "assistant-message-copied"
                  });
                } else {
                  throw new Error("Failed to copy to clipboard");
                }
              }).catch(() => {
                toast({
                  content: "Failed to copy to clipboard",
                  id: "assistant-message-copied-failed"
                });
              });
            },
            children: "Copy"
          }),
          authStatus === "authenticated" && /* @__PURE__ */ jsx(ButtonMessageAction, {
            size: "sm",
            isLoading: isSavingAsNote,
            onClick: (e) => {
              e.stopPropagation();
              const html = replaceAnchorsWithNotepadExtensionEmbedElement(parseMarkdown(content, {
                includeEmptyLines: false
              }));
              if (!html) {
                return;
              }
              const title = `Saved assistant message ${dayjs().format("YYYY-MM-DD HH:mm")}`;
              mutateCreateResource({
                data: {
                  createType: "note",
                  body: {
                    name: title,
                    parentId: destinationParentFolder.id,
                    ydoc: Array.from(getYdocBinaryStateFromHTML(html)),
                    tags: []
                  },
                  optimisticData: {
                    tags: [],
                    parent: destinationParentFolder,
                    preview: {
                      content: html
                    }
                  }
                }
              }, {
                onSuccess: () => {
                  addMessageSentAsAssistant({
                    content: "Saved as a note! You can find it in your inbox."
                  });
                }
              });
            },
            children: "Save as note"
          })
        ]
      })
    })
  });
};
const ButtonMessageAction = styled(Button).withConfig({
  displayName: "ChatbotMessageActions__ButtonMessageAction",
  componentId: "sc-a34732c0-1"
})([
  `background:rgba(`,
  `,0.1);color:`,
  `;`,
  ` `,
  ` `,
  ``
], cssVar["color-bg-primary-reverse-rgb"], cssVar["color-text-primary"], (p) => p.isLoading && css([
  `color:transparent !important;`
]), (p) => p.diffApplyType === "accept" && css([
  `background:rgba(70,205,36,0.35);color:#76ff54;`
]), (p) => p.diffApplyType === "reject" && css([
  `background:rgba(255,0,0,0.35);color:#ffbbbb;`
]));

export { ChatbotMessageActions };
//# sourceMappingURL=ChatbotMessageActions.js.map
